import { memo, useMemo } from 'react';

import { useTranslation } from '@hooks/useTranslation';
import CloseIcon from '@assets/icons/CloseIcon';
import type { FilterFieldConfig, FilterValue, FilterValues } from '@/types/filters';
import styles from './AppliedFiltersBar.module.scss';

// ============================================
// HELPERS
// ============================================

/** Resolve a filter value to a display string */
function formatFilterValue(
  value: FilterValue,
  config: FilterFieldConfig | undefined,
  t: (key: string, fallback: string) => string,
): string {
  if (typeof value === 'boolean') {
    return value ? t('K_YES', 'Yes') : t('K_NO', 'No');
  }

  if (typeof value === 'string') {
    // Try to resolve from options
    if (config?.options) {
      const opt = config.options.find((o) => o.value === value);
      if (opt) return opt.label;
    }
    return value;
  }

  if (Array.isArray(value)) {
    if (config?.options) {
      const labels = value
        .map((v) => config.options?.find((o) => o.value === v)?.label ?? v)
        .filter(Boolean);
      if (labels.length <= 2) return labels.join(', ');
      return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
    }
    return value.join(', ');
  }

  if (typeof value === 'object') {
    const range = value as { gte?: string; lte?: string };
    const parts: string[] = [];
    if (range.gte) parts.push(formatDate(range.gte));
    if (range.lte) parts.push(formatDate(range.lte));
    return parts.join(' – ');
  }

  return String(value);
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ============================================
// TRASH ICON
// ============================================

function TrashIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3 6H5H21M8 6V4C8 3.47 8.21 2.96 8.59 2.59C8.96 2.21 9.47 2 10 2H14C14.53 2 15.04 2.21 15.41 2.59C15.79 2.96 16 3.47 16 4V6M19 6V20C19 20.53 18.79 21.04 18.41 21.41C18.04 21.79 17.53 22 17 22H7C6.47 22 5.96 21.79 5.59 21.41C5.21 21.04 5 20.53 5 20V6H19Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

// ============================================
// TYPES
// ============================================

export interface AppliedFiltersBarProps {
  /** Filter field configs — needed to resolve fieldKey → label */
  fields: FilterFieldConfig[];
  /** Currently applied filter values */
  values: FilterValues;
  /** Remove a single filter by fieldKey */
  onRemove: (fieldKey: string) => void;
  /** Clear all filters */
  onClearAll: () => void;
}

// ============================================
// COMPONENT
// ============================================

function AppliedFiltersBar({ fields, values, onRemove, onClearAll }: AppliedFiltersBarProps) {
  const { t } = useTranslation();

  const chips = useMemo(() => {
    return Object.entries(values).map(([key, value]) => {
      const config = fields.find((f) => f.fieldKey === key);
      return {
        key,
        label: config?.label ?? key,
        displayValue: formatFilterValue(value, config, t),
      };
    });
  }, [values, fields, t]);

  if (chips.length === 0) return null;

  return (
    <div className={styles.bar}>
      <div className={styles.chipList}>
        {chips.map((chip) => (
          <div key={chip.key} className={styles.chip}>
            <span className={styles.chipLabel}>{chip.label}:</span>
            <span className={styles.chipValue}>{chip.displayValue}</span>
            <button
              type="button"
              className={styles.chipRemove}
              onClick={() => onRemove(chip.key)}
              aria-label={`Remove ${chip.label} filter`}
            >
              <CloseIcon />
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        className={styles.clearAll}
        onClick={onClearAll}
        aria-label="Clear all filters"
        title="Clear all filters"
      >
        <TrashIcon />
      </button>
    </div>
  );
}

export default memo(AppliedFiltersBar);
